import { useState, useEffect, useRef, useCallback } from 'react';
import { GOOGLE_CLIENT_ID, API_URL } from '../utils';

export const useAuth = () => {
  console.log('useAuth hook initialized');

  const [backendToken, setBackendToken] = useState<string | null>(() => {
    const token = window.localStorage.getItem('backendToken');
    console.log('Initial backendToken from localStorage:', token ? 'present' : 'null');
    return token;
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState<boolean>(!!window.google);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [loginRefreshKey, setLoginRefreshKey] = useState(0);
  const [isExchangingToken, setIsExchangingToken] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Exchange Google token for backend JWT
  const exchangeGoogleToken = useCallback(async (googleToken: string) => {
    if (isExchangingToken) {
      console.log('Token exchange already in progress, skipping...');
      return;
    }

    setIsExchangingToken(true);
    
    try {
      console.log('Exchanging Google token for backend JWT...');
      
      const response = await fetch(`${API_URL}/auth/exchange-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ googleToken }),
      });

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('Token exchange successful');
      
      // Store the backend JWT
      setBackendToken(data.token);
      window.localStorage.setItem('backendToken', data.token);
      
      // Clear any expired session flags
      setSessionExpired(false);
      setForbidden(false);
      
    } catch (error) {
      console.error('Token exchange error:', error);
      // If token exchange fails, clear any stored tokens
      setBackendToken(null);
      window.localStorage.removeItem('backendToken');
      setSessionExpired(true);
    } finally {
      setIsExchangingToken(false);
    }
  }, [isExchangingToken]);

  // Helper to add Authorization header and handle forbidden/expired
  const fetchWithAuth = useCallback(async (url: string, options: any = {}) => {
    console.log('fetchWithAuth called for URL:', url);
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          Authorization: backendToken ? `Bearer ${backendToken}` : '',
        },
      });
      
      console.log('fetchWithAuth response status:', response.status);
      
      if (response.status === 403) {
        console.log('Received forbidden (403) response');
        setForbidden(true);
      }
      
      if (response.status === 401) {
        console.log('Received unauthorized (401) response - clearing session');
        setSessionExpired(true);
        setBackendToken(null);
        window.localStorage.removeItem('backendToken');
      }
      
      return response;
    } catch (error: any) {
      console.error('fetchWithAuth error:', error);

      // Handle network errors that typically occur when backend is sleeping/unavailable
      if (
        error.name === 'TypeError' && 
        (error.message.includes('fetch') || error.message.includes('Failed to fetch'))
      ) {
        console.log('Backend appears to be unreachable - network error');
        const networkError = new Error('Service temporarily unavailable');
        (networkError as any).isServiceUnavailable = true;
        throw networkError;
      }
      
      // Handle timeout errors
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        console.log('Request timed out');
        const timeoutError = new Error('Service temporarily unavailable');
        (timeoutError as any).isServiceUnavailable = true;
        throw timeoutError;
      }
      
      // Handle connection refused, network unreachable, etc.
      if (
        error.message.includes('network') || 
        error.message.includes('connection') ||
        error.message.includes('ECONNREFUSED') ||
        error.message.includes('ENOTFOUND') ||
        error.message.includes('ENETUNREACH') ||
        error.message.includes('ERR_NETWORK') ||
        error.message.includes('ERR_INTERNET_DISCONNECTED')
      ) {
        console.log('Network connection error detected');
        const networkError = new Error('Service temporarily unavailable');
        (networkError as any).isServiceUnavailable = true;
        throw networkError;
      }
      
      throw error;
    }
  }, [backendToken]);

  // When backendToken changes from null to a value, force data refresh
  useEffect(() => {
    if (backendToken) {
      console.log('backendToken changed - triggering data refresh');
      setLoginRefreshKey((k: number) => k + 1);
    }
  }, [backendToken]);

  // Retry logic for Google script
  useEffect(() => {
    console.log('Checking Google script status:', window.google ? 'loaded' : 'not loaded');
    if (window.google) {
      setGoogleScriptLoaded(true);
      setScriptError(null);
    } else {
      const timeout = setTimeout(() => {
        if (!window.google) {
          console.log('Google script failed to load after timeout');
          setGoogleScriptLoaded(false);
          setScriptError('Google Sign-In script failed to load. Please check your internet connection or try refreshing.');
        }
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, []);

  // Initialize Google Sign-In
  useEffect(() => {
    console.log('Google Sign-In initialization check:', {
      backendToken: !!backendToken,
      googleLoaded: !!window.google,
      buttonRef: !!googleButtonRef.current
    });

    if (!backendToken && window.google && googleButtonRef.current) {
      console.log('Initializing Google Sign-In');
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: any) => {
          console.log('Received Google Sign-In response');
          await exchangeGoogleToken(response.credential);
        },
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
      });
    }
  }, [backendToken, googleScriptLoaded, exchangeGoogleToken]);

  const handleLogout = useCallback(() => {
    console.log('Handling logout');
    setBackendToken(null);
    window.localStorage.removeItem('backendToken');
    // Also clear the old idToken if it exists
    window.localStorage.removeItem('idToken');
    window.location.reload();
  }, []);

  return {
    idToken: backendToken, // Keep the same interface for compatibility
    setIdToken: setBackendToken, // Keep the same interface for compatibility
    fetchWithAuth,
    handleLogout,
    sessionExpired,
    forbidden,
    googleButtonRef,
    scriptError,
    loginRefreshKey,
    setLoginRefreshKey,
    isExchangingToken,
    exchangeGoogleToken
  };
}; 